import { FC, useState } from 'react';
import { sumBy } from 'lodash';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Typography,
} from '@mui/material';
import { AllocationRule } from 'further-types/lp/allocations';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { FundingSource } from 'further-types/lp/investment';
import { Column, Row } from 'further-ui/components/Layout';
import StaticField from 'further-ui/components/StaticField';
import { FundingSourceLabels } from 'further-ui/labels';
import {
  CalendarDay,
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import AllocationsManager, {
  Allocation,
} from 'components/new/AllocationsManager';
import { useLayoutContext } from 'contexts/LayoutContext';

export type InvestmentHoldingClass = {
  isNewHoldingClass: boolean;
  holdingClassId?: string;
  name: string;
  currency: CurrencyCode;
  investmentAmountTcy: number;
  numberOfUnits: number;
  unitPriceTcy: number;
};

export type InvestmentProposal = {
  dealName: string;
  dealCompletionDate: string;
  fundingSource: FundingSource;
  allocationRule: string;
  isNewAsset: boolean;
  assetId?: string;
  assetName?: string;
  asset?: {
    name: string;
    fullLegalName: string;
    baseCurrency: CurrencyCode;
    registeredAddressLine1?: string;
    registeredAddressLine2?: string;
    registeredAddressTownCity?: string;
    registeredAddressPostalCode?: string;
    registeredAddressCountry?: string;
    registeredNumber?: string;
    registrationCountry?: string;
    description?: string;
    websiteUrl?: string;
    linkedinUrl?: string;
    twitterUrl?: string;
    stage?: string;
    sector?: string;
    logoUrl?: string;
  };
  holdingClasses: Array<InvestmentHoldingClass>;
  allocations: Array<{
    commitmentId: string;
    allocationAmountTcy: number;
    allocationPercentage: number;
    excludeFromAllocation: boolean;
  }>;
};

type Props = {
  proposal: InvestmentProposal;
  vehicleId: string;
  onApprove: (proposal: InvestmentProposal) => void;
  onReject: () => void;
  isExecuting: boolean;
};

const InvestmentProposalCard: FC<Props> = ({
  proposal,
  vehicleId,
  onApprove,
  onReject,
  isExecuting,
}) => {
  const { vehicle } = useLayoutContext();
  const [allocations, setAllocations] = useState<Array<Allocation>>(
    proposal.allocations,
  );
  const [allocationRule, setAllocationRule] = useState<AllocationRule>(
    proposal.allocationRule as AllocationRule,
  );

  const completionDate = proposal.dealCompletionDate
    ? new CalendarDay(proposal.dealCompletionDate)
    : null;

  const currency =
    proposal.holdingClasses[0]?.currency ?? vehicle?.baseCurrency;
  const baseCurrency = vehicle?.baseCurrency;

  const totalInvestmentAmountTcy = sumBy(
    proposal.holdingClasses,
    'investmentAmountTcy',
  );

  const assetDisplayName = proposal.isNewAsset
    ? proposal.asset?.name ?? proposal.asset?.fullLegalName ?? 'New company'
    : proposal.assetName ?? 'Existing company';

  const handleApprove = () => {
    onApprove({
      ...proposal,
      allocations,
      allocationRule,
    });
  };

  const asset = proposal.asset;
  const hasProfileInfo =
    proposal.isNewAsset &&
    asset &&
    (asset.description ||
      asset.websiteUrl ||
      asset.linkedinUrl ||
      asset.twitterUrl ||
      asset.stage ||
      asset.sector ||
      asset.logoUrl);

  return (
    <Card variant="outlined" sx={{ mt: 1, mb: 1 }}>
      <CardContent>
        <Column spacing="md">
          <Typography variant="h5">Proposed Investment</Typography>

          <Row spacing="lg">
            <Box sx={{ flex: 1 }}>
              <Column spacing="sm">
                {asset?.logoUrl && (
                  <Box
                    component="img"
                    src={asset.logoUrl}
                    alt={assetDisplayName}
                    sx={{
                      maxHeight: 48,
                      maxWidth: 120,
                      objectFit: 'contain',
                      mb: 0.5,
                    }}
                  />
                )}
                <StaticField label="Company (Asset)">
                  <Row spacing="xs" centered>
                    <Typography variant="body1">{assetDisplayName}</Typography>
                    {proposal.isNewAsset && (
                      <Chip label="New" size="small" color="info" />
                    )}
                  </Row>
                </StaticField>
                {proposal.isNewAsset && asset?.fullLegalName && (
                  <StaticField label="Legal Name">
                    <Typography variant="body1">
                      {asset.fullLegalName}
                    </Typography>
                  </StaticField>
                )}
                {proposal.isNewAsset && asset?.baseCurrency && (
                  <StaticField label="Company Currency">
                    <Typography variant="body1">
                      {asset.baseCurrency}
                    </Typography>
                  </StaticField>
                )}
                {proposal.isNewAsset && asset?.registeredAddressLine1 && (
                  <StaticField label="Registered Address">
                    <Typography variant="body1">
                      {[
                        asset.registeredAddressLine1,
                        asset.registeredAddressLine2,
                        asset.registeredAddressTownCity,
                        asset.registeredAddressPostalCode,
                        asset.registeredAddressCountry,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  </StaticField>
                )}
                {proposal.isNewAsset && asset?.registeredNumber && (
                  <StaticField label="Registration Number">
                    <Typography variant="body1">
                      {asset.registeredNumber}
                    </Typography>
                  </StaticField>
                )}
              </Column>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Column spacing="sm">
                <StaticField label="Deal Name">
                  <Typography variant="body1">{proposal.dealName}</Typography>
                </StaticField>
                <StaticField label="Deal Completion Date">
                  <Typography variant="body1">
                    {completionDate?.toLabel() ?? proposal.dealCompletionDate}
                  </Typography>
                </StaticField>
                <StaticField label="Funding Source">
                  <Typography variant="body1">
                    {FundingSourceLabels[proposal.fundingSource]}
                  </Typography>
                </StaticField>
                <StaticField label="Total Investment Amount">
                  <Typography variant="h6">
                    {numberToCurrencyString(totalInvestmentAmountTcy, {
                      currency,
                    })}
                  </Typography>
                </StaticField>
              </Column>
            </Box>
          </Row>

          {hasProfileInfo && (
            <>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Company Profile
              </Typography>
              <Row spacing="lg">
                <Box sx={{ flex: 1 }}>
                  <Column spacing="sm">
                    {asset?.description && (
                      <StaticField label="Description">
                        <Typography variant="body1">
                          {asset.description}
                        </Typography>
                      </StaticField>
                    )}
                    {(asset?.stage || asset?.sector) && (
                      <StaticField label="Stage / Sector">
                        <Row spacing="xs">
                          {asset.stage && (
                            <Chip
                              label={asset.stage}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {asset.sector && (
                            <Chip
                              label={asset.sector}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Row>
                      </StaticField>
                    )}
                  </Column>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Column spacing="sm">
                    {asset?.websiteUrl && (
                      <StaticField label="Website">
                        <Link
                          href={asset.websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body1"
                        >
                          {asset.websiteUrl}
                        </Link>
                      </StaticField>
                    )}
                    {asset?.linkedinUrl && (
                      <StaticField label="LinkedIn">
                        <Link
                          href={asset.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body1"
                        >
                          {asset.linkedinUrl}
                        </Link>
                      </StaticField>
                    )}
                    {asset?.twitterUrl && (
                      <StaticField label="Twitter / X">
                        <Link
                          href={asset.twitterUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body1"
                        >
                          {asset.twitterUrl}
                        </Link>
                      </StaticField>
                    )}
                    {asset?.logoUrl && (
                      <StaticField label="Logo">
                        <Typography variant="body2" color="text.secondary">
                          Will be uploaded automatically on approval.
                        </Typography>
                      </StaticField>
                    )}
                  </Column>
                </Box>
              </Row>
            </>
          )}

          <Typography variant="h6" sx={{ mt: 1 }}>
            Holding Classes
          </Typography>

          <Column spacing="sm">
            {proposal.holdingClasses.map((hc, index) => (
              <Card key={index} variant="outlined" sx={{ p: 2 }}>
                <Row spacing="lg">
                  <Box sx={{ flex: 1 }}>
                    <Column spacing="xs">
                      <Row spacing="xs" centered>
                        <Typography variant="subtitle2">{hc.name}</Typography>
                        {hc.isNewHoldingClass && (
                          <Chip label="New" size="small" color="info" />
                        )}
                      </Row>
                      <Typography variant="body2" color="text.secondary">
                        {hc.currency}
                      </Typography>
                    </Column>
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <Row spacing="lg">
                      <StaticField label="Investment Amount">
                        <Typography variant="body2">
                          {numberToCurrencyString(hc.investmentAmountTcy, {
                            currency: hc.currency,
                          })}
                        </Typography>
                      </StaticField>
                      <StaticField label="Units">
                        <Typography variant="body2">
                          {numberToDisplayString(hc.numberOfUnits)}
                        </Typography>
                      </StaticField>
                      <StaticField label="Unit Price">
                        <Typography variant="body2">
                          {numberToCurrencyString(hc.unitPriceTcy, {
                            currency: hc.currency,
                          })}
                        </Typography>
                      </StaticField>
                    </Row>
                  </Box>
                </Row>
              </Card>
            ))}
          </Column>

          <Typography variant="h6" sx={{ mt: 2 }}>
            Allocations
          </Typography>
          {completionDate && currency && baseCurrency && (
            <AllocationsManager
              vehicleId={vehicleId}
              allocationRule={allocationRule}
              onAllocationRuleChange={setAllocationRule}
              onAllocationsChange={setAllocations}
              allocatedActionLabel="Investment"
              currency={currency}
              baseCurrency={baseCurrency}
              allocations={allocations}
              allocatedAmountTcy={totalInvestmentAmountTcy}
              date={completionDate}
            />
          )}

          <Row spacing="md" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApprove}
              disabled={isExecuting}
            >
              {isExecuting ? 'Creating...' : 'Approve & create draft'}
            </Button>
            <Button
              variant="outlined"
              onClick={onReject}
              disabled={isExecuting}
            >
              Cancel
            </Button>
          </Row>
        </Column>
      </CardContent>
    </Card>
  );
};

export default InvestmentProposalCard;
